/**
 * NetworkBadge / ConfidenceBadge — shared intelligence chips used across
 * the admin globe console, country drilldown drawer, and per-visitor
 * dossier drawer.
 *
 * The classification taxonomy mirrors the backend
 * (`_network_bucket` in `audience_live.py`):
 *
 *   • Residential ISP        → green/teal, location is trustworthy
 *   • Mobile Carrier         → amber, carrier-aggregated location
 *   • Apple Private Relay    → violet, location obfuscated
 *   • Cloudflare Network     → violet, location obfuscated
 *   • VPN / Proxy Suspected  → red, location is masked
 *   • Unknown                → grey, no signal
 *
 * Location confidence:
 *   • high   → green
 *   • medium → amber
 *   • low    → red
 *
 * Visual language matches the existing dossier-card aesthetic
 * (mono labels, 0.25em tracking, semi-transparent borders).
 */
import React from "react";
import { ShieldCheck, Smartphone, Cloud, ShieldAlert, EyeOff, Wifi } from "lucide-react";

// Map raw classifier string -> visual treatment.
const NETWORK_STYLE = {
  "Residential ISP": {
    label: "RESIDENTIAL ISP",
    color: "#5DD4B3",
    border: "#5DD4B355",
    bg: "#5DD4B311",
    Icon: Wifi,
    bucket: "residential",
  },
  "Mobile Carrier": {
    label: "MOBILE CARRIER",
    color: "#CBB892",
    border: "#CBB89255",
    bg: "#CBB89211",
    Icon: Smartphone,
    bucket: "mobile",
  },
  "Apple Private Relay": {
    label: "APPLE RELAY",
    color: "#B59CE0",
    border: "#B59CE055",
    bg: "#B59CE011",
    Icon: EyeOff,
    bucket: "relay",
  },
  "Cloudflare Network": {
    label: "CLOUDFLARE RELAY",
    color: "#B59CE0",
    border: "#B59CE055",
    bg: "#B59CE011",
    Icon: Cloud,
    bucket: "relay",
  },
  "VPN / Proxy Suspected": {
    label: "VPN / PROXY",
    color: "#D92D20",
    border: "#D92D2055",
    bg: "#D92D2011",
    Icon: ShieldAlert,
    bucket: "vpn",
  },
  "Unknown": {
    label: "NO SIGNAL",
    color: "#757065",
    border: "#45403855",
    bg: "#1a1815",
    Icon: ShieldCheck,
    bucket: "unknown",
  },
};

export const networkStyleFor = (type) =>
  NETWORK_STYLE[type] || NETWORK_STYLE["Unknown"];

export const NetworkBadge = ({ type, testid, small = false }) => {
  const s = networkStyleFor(type);
  const Icon = s.Icon;
  const pad = small ? "px-1.5 py-0.5" : "px-2 py-1";
  const txt = small ? "text-[8px]" : "text-[9px]";
  const iconSize = small ? 10 : 11;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 border font-mono ${txt} uppercase tracking-[0.25em] ${pad}`}
      style={{ color: s.color, borderColor: s.border, background: s.bg }}
      title={`Network classification: ${type || "Unknown"}`}
    >
      <Icon size={iconSize} strokeWidth={1.5} />
      {s.label}
    </span>
  );
};

const CONFIDENCE_STYLE = {
  high:   { label: "HIGH CONFIDENCE",   color: "#5DD4B3", border: "#5DD4B355", bg: "#5DD4B311", dot: "#5DD4B3" },
  medium: { label: "MEDIUM CONFIDENCE", color: "#CBB892", border: "#CBB89255", bg: "#CBB89211", dot: "#CBB892" },
  low:    { label: "LOW CONFIDENCE",    color: "#D92D20", border: "#D92D2055", bg: "#D92D2011", dot: "#D92D20" },
};

export const confidenceStyleFor = (level) =>
  CONFIDENCE_STYLE[(level || "low").toLowerCase()] || CONFIDENCE_STYLE.low;

export const ConfidenceBadge = ({ level, testid, small = false }) => {
  const s = confidenceStyleFor(level);
  const pad = small ? "px-1.5 py-0.5" : "px-2 py-1";
  const txt = small ? "text-[8px]" : "text-[9px]";
  const dotSize = small ? 4 : 5;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 border font-mono ${txt} uppercase tracking-[0.25em] ${pad}`}
      style={{ color: s.color, borderColor: s.border, background: s.bg }}
      title={`Location confidence: ${level || "low"}`}
    >
      <span
        className="inline-block rounded-full"
        style={{ width: dotSize, height: dotSize, background: s.dot, boxShadow: `0 0 6px ${s.dot}` }}
      />
      {s.label}
    </span>
  );
};

/**
 * Helper for opacity scaling on globe beacons based on confidence —
 * LOW = dimmed, MEDIUM = mid, HIGH = full. Returns a value between
 * 0.35 and 1.0 so the dimming is visible but not invisible.
 */
export const beaconOpacityFor = (level) => {
  const l = (level || "low").toLowerCase();
  if (l === "high") return 1.0;
  if (l === "medium") return 0.7;
  return 0.4;
};

export default NetworkBadge;
